/* Agape store finder — Leaflet map + nearest-store list.
 *
 * Store data is always live, read from the section's configured endpoint (the website's
 * /api/store-map/stores route, which allows the storefront origin via CORS). There is no
 * bundled copy: if the request fails the finder shows its error message rather than
 * risking stale store locations.
 */
(function () {
  'use strict';

  var EARTH_RADIUS_MI = 3958.7613;

  /* Drive-time bracketing. Only stores this close are ever routed; everything past the
     second bracket is ranked by straight-line mileage alone. */
  var BRACKET_ONE_MILES = 20;
  var BRACKET_TWO_MILES = 40;
  /* If the close bracket routes at least this many stores, the wider bracket is skipped —
     the quickest store is effectively certain to already be in hand. */
  var BRACKET_ONE_SUFFICIENT_RESULTS = 3;
  /* Mirrors the server cap, which is bounded by Mapbox's 25-coordinate matrix limit. */
  var MAX_TRAVEL_TIME_DESTINATIONS = 24;

  function toRadians(degrees) {
    return (degrees * Math.PI) / 180;
  }

  /** Great-circle distance in miles. */
  function distanceMiles(fromLat, fromLng, toLat, toLng) {
    var deltaLat = toRadians(toLat - fromLat);
    var deltaLng = toRadians(toLng - fromLng);
    var a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(toRadians(fromLat)) *
        Math.cos(toRadians(toLat)) *
        Math.sin(deltaLng / 2) *
        Math.sin(deltaLng / 2);
    return EARTH_RADIUS_MI * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatDistance(miles) {
    if (miles < 10) return miles.toFixed(1) + ' mi';
    return Math.round(miles) + ' mi';
  }

  function formatDuration(seconds) {
    var minutes = Math.round(seconds / 60);
    if (minutes < 1) return '<1 min';
    if (minutes < 60) return minutes + ' min';
    var hours = Math.floor(minutes / 60);
    var remainder = minutes % 60;
    return remainder === 0 ? hours + ' hr' : hours + ' hr ' + remainder + ' min';
  }

  /** Matches the server's rounding so both sides key their caches identically. */
  function roundCoordinate(value) {
    return Math.round(value * 1000) / 1000;
  }

  function originCacheKey(origin) {
    return roundCoordinate(origin.latitude) + ',' + roundCoordinate(origin.longitude);
  }

  function formatLocality(store) {
    return [store.city, store.region].filter(Boolean).join(', ');
  }

  function formatAddress(store) {
    var locality = [store.city, store.region, store.postalCode].filter(Boolean).join(', ');
    return [store.addressLine1, store.addressLine2, locality].filter(Boolean).join(', ');
  }

  /** Google Maps' universal directions URL opens cleanly on iOS, Android, and desktop. */
  function directionsUrl(store) {
    var label = [store.name, formatAddress(store)].filter(Boolean).join(', ').trim();
    var destination = label || store.latitude + ',' + store.longitude;
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(destination);
  }

  function hasCoordinates(store) {
    return (
      store &&
      typeof store.latitude === 'number' &&
      isFinite(store.latitude) &&
      typeof store.longitude === 'number' &&
      isFinite(store.longitude)
    );
  }

  function isRenderableStore(store) {
    return (
      store &&
      typeof store.name === 'string' &&
      store.name.length > 0 &&
      typeof store.latitude === 'number' &&
      typeof store.longitude === 'number' &&
      (store.status === 'live' || store.status === 'coming_soon')
    );
  }

  function parseConfig(root) {
    var node = root.querySelector('[data-store-finder-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function StoreFinder(root) {
    this.root = root;
    this.config = parseConfig(root);
    if (!this.config) return;

    this.strings = this.config.strings || {};
    this.mapEl = root.querySelector('[data-store-finder-map]');
    this.listEl = root.querySelector('[data-store-finder-list]');
    this.emptyEl = root.querySelector('[data-store-finder-empty]');
    this.statusEl = root.querySelector('[data-store-finder-status]');
    this.locateButton = root.querySelector('[data-store-finder-locate]');
    this.locateLabel = root.querySelector('[data-store-finder-locate-label]');
    this.searchForm = root.querySelector('[data-store-finder-search]');
    this.searchInput = root.querySelector('[data-store-finder-search-input]');

    this.stores = [];
    this.visibleStores = [];
    this.markers = {};
    this.origin = null;
    this.originMarker = null;
    this.selectedId = null;
    this.selectionIsAuto = false;
    this.nearestId = null;
    this.searchTerm = '';
    this.zip3 = null;
    this.locating = false;
    this.loadFailed = false;
    this.travelTimes = {};
    this.travelTimeRequestId = 0;
    this.travelTimesUnavailable = false;

    this.bindEvents();
    this.initMap();
    this.loadStores();
  }

  StoreFinder.prototype.bindEvents = function () {
    var self = this;

    if (this.locateButton) {
      this.locateButton.addEventListener('click', function () {
        self.requestLocation();
      });
    }

    if (this.searchForm) {
      this.searchForm.addEventListener('submit', function (event) {
        event.preventDefault();
        self.applySearch(self.searchInput ? self.searchInput.value : '');
      });
    }

    if (this.searchInput) {
      var debounce = null;
      this.searchInput.addEventListener('input', function () {
        var value = self.searchInput.value;
        window.clearTimeout(debounce);
        debounce = window.setTimeout(function () {
          self.applySearch(value);
        }, 200);
      });
    }
  };

  StoreFinder.prototype.initMap = function () {
    if (!this.mapEl || typeof window.L === 'undefined') return;

    var latitude = Number(this.config.defaultLatitude);
    var longitude = Number(this.config.defaultLongitude);
    var zoom = Number(this.config.defaultZoom);

    this.map = window.L.map(this.mapEl, {
      zoomControl: true,
      scrollWheelZoom: false,
      attributionControl: true
    }).setView(
      [isFinite(latitude) ? latitude : 39.5, isFinite(longitude) ? longitude : -98.35],
      isFinite(zoom) ? zoom : 4
    );

    window.L.tileLayer(this.config.tileUrl, {
      attribution: this.config.tileAttribution,
      maxZoom: 19,
      detectRetina: true
    }).addTo(this.map);

    // Scroll-wheel zoom is off by default so the page still scrolls past the map;
    // clicking into the map opts in, and leaving it opts back out.
    var self = this;
    this.map.on('click focus', function () {
      self.map.scrollWheelZoom.enable();
    });
    this.map.on('mouseout blur', function () {
      self.map.scrollWheelZoom.disable();
    });
  };

  /**
   * Local previews read the bundled fixture instead of the store API.
   *
   * The API only allows the production storefront origin, so `shopify theme dev` on
   * 127.0.0.1 gets a CORS failure and the finder renders its "temporarily unavailable"
   * error with nothing on the map to click. The switch is the hostname alone — a real
   * visitor is never on one of these, so fixture data cannot reach production no matter
   * what is in the URL. `?mock_stores=0` opts back out locally to exercise the real
   * fetch (and its error path).
   */
  function isLocalHost() {
    var host = window.location.hostname;
    return (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '[::1]' ||
      host === '::1' ||
      /\.local$/.test(host)
    );
  }

  StoreFinder.prototype.usingMockStores = function () {
    if (!isLocalHost()) return false;
    if (/(^|[?&])mock_stores=0(&|$)/.test(window.location.search)) return false;
    return Boolean((this.config.mockEndpoint || '').trim());
  };

  StoreFinder.prototype.loadStores = function () {
    var self = this;
    var mock = this.usingMockStores();
    var endpoint = mock
      ? this.config.mockEndpoint.trim()
      : (this.config.endpoint || '').trim();

    if (mock && window.console) {
      window.console.info(
        '[store-finder] local host — using mock store data; ?mock_stores=0 to use the live API'
      );
    }

    if (!endpoint) {
      this.loadFailed = true;
      this.setStatus(this.strings.loadError || '');
      this.render();
      return;
    }

    this.fetchStores(endpoint)
      .then(function (stores) {
        self.loadFailed = false;
        self.stores = stores.filter(isRenderableStore).filter(function (store) {
          return self.config.showComingSoon || store.status === 'live';
        });
        self.render();
      })
      .catch(function () {
        self.loadFailed = true;
        self.stores = [];
        self.setStatus(self.strings.loadError || '');
        self.render();
      });
  };

  StoreFinder.prototype.fetchStores = function (url) {
    return fetch(url, { method: 'GET' })
      .then(function (response) {
        if (!response.ok) throw new Error('Failed to load stores (' + response.status + ')');
        return response.json();
      })
      .then(function (payload) {
        if (!payload || !Array.isArray(payload.stores)) {
          throw new Error('Store response did not include a stores array');
        }
        return payload.stores;
      });
  };

  StoreFinder.prototype.setStatus = function (message) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message || '';
    this.statusEl.hidden = !message;
  };

  StoreFinder.prototype.requestLocation = function () {
    var self = this;
    if (this.locating) return;

    if (!navigator.geolocation) {
      this.setStatus(this.strings.locationFailed || '');
      return;
    }

    this.locating = true;
    if (this.locateLabel) this.locateLabel.textContent = this.strings.locating || 'Locating…';
    if (this.locateButton) this.locateButton.disabled = true;
    this.setStatus('');

    navigator.geolocation.getCurrentPosition(
      function (position) {
        self.finishLocating();
        self.setOrigin(position.coords.latitude, position.coords.longitude, true);
      },
      function (error) {
        self.finishLocating();
        var denied = error && error.code === 1;
        self.setStatus(
          (denied ? self.strings.locationDenied : self.strings.locationFailed) || ''
        );
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  StoreFinder.prototype.finishLocating = function () {
    this.locating = false;
    if (this.locateLabel) this.locateLabel.textContent = this.strings.locate || 'Use my location';
    if (this.locateButton) this.locateButton.disabled = false;
  };

  StoreFinder.prototype.setOrigin = function (latitude, longitude, showMarker) {
    this.origin = { latitude: latitude, longitude: longitude };
    // Drive times are measured from the origin, so a new origin invalidates all of them.
    this.travelTimes = {};

    if (this.map) {
      if (this.originMarker) {
        this.map.removeLayer(this.originMarker);
        this.originMarker = null;
      }
      if (showMarker && window.L) {
        this.originMarker = window.L.marker([latitude, longitude], {
          icon: window.L.divIcon({
            className: 'agape-store-finder__you-marker',
            html: '<span class="agape-store-finder__you-dot"></span>',
            iconSize: [18, 18],
            iconAnchor: [9, 9]
          }),
          keyboard: false,
          interactive: false
        }).addTo(this.map);
      }
    }

    this.render();

    // Frame the nearest store plus its neighbours, then open the nearest one — the
    // visitor gets their answer without losing the surrounding options.
    if (this.visibleStores.length > 0) {
      this.fitToNearest();
      // nearestId, not visibleStores[0]: an earlier pick sits at the head of the list.
      this.selectStore(this.nearestId || this.visibleStores[0].storeId, false, true);
    } else {
      this.fitToVisible();
    }

    this.loadTravelTimes();
  };

  /**
   * Re-ranks nearby stores by drive time.
   *
   * Straight-line distance still picks the candidates — only a bracket of already-close
   * stores is ever routed, which is what keeps this cheap. The first bracket is the
   * nearest stores within BRACKET_ONE_MILES; the second only runs if the first came back
   * thin, since a handful of routed stores within 20 miles almost always contains the
   * quickest one. Everything beyond that stays ranked by pure mileage.
   *
   * This runs after the list has already rendered, so the finder is never waiting on it.
   */
  StoreFinder.prototype.loadTravelTimes = function () {
    var self = this;
    var endpoint = (this.config.travelTimeEndpoint || '').trim();
    if (!endpoint || !this.origin || this.travelTimesUnavailable) return;
    // Mock store ids mean nothing to the routing API — leave the list on mileage ranking.
    if (this.usingMockStores()) return;

    var requestId = ++this.travelTimeRequestId;
    var origin = this.origin;
    var cached = this.readCachedTravelTimes(origin);
    if (cached) {
      this.travelTimes = cached;
      this.applyTravelTimeRanking();
      return;
    }

    var candidates = this.stores
      .filter(hasCoordinates)
      .map(function (store) {
        return {
          store: store,
          miles: distanceMiles(origin.latitude, origin.longitude, store.latitude, store.longitude)
        };
      })
      .sort(function (left, right) {
        return left.miles - right.miles;
      });

    var bracketOne = candidates
      .filter(function (entry) {
        return entry.miles <= BRACKET_ONE_MILES;
      })
      .slice(0, MAX_TRAVEL_TIME_DESTINATIONS);

    this.requestTravelTimes(endpoint, origin, bracketOne, requestId)
      .then(function (results) {
        if (requestId !== self.travelTimeRequestId) return null;

        // Enough routed stores in the close bracket — the quickest is already among them,
        // so skip the wider bracket entirely rather than paying for it.
        if (results.length >= BRACKET_ONE_SUFFICIENT_RESULTS) return results;

        var alreadyRequested = {};
        bracketOne.forEach(function (entry) {
          alreadyRequested[entry.store.storeId] = true;
        });
        var bracketTwo = candidates
          .filter(function (entry) {
            return entry.miles > BRACKET_ONE_MILES && entry.miles <= BRACKET_TWO_MILES;
          })
          .filter(function (entry) {
            return !alreadyRequested[entry.store.storeId];
          })
          .slice(0, MAX_TRAVEL_TIME_DESTINATIONS);

        if (bracketTwo.length === 0) return results;

        return self
          .requestTravelTimes(endpoint, origin, bracketTwo, requestId)
          .then(function (more) {
            return results.concat(more);
          });
      })
      .then(function (results) {
        if (results === null || requestId !== self.travelTimeRequestId) return;

        var byStoreId = {};
        results.forEach(function (result) {
          byStoreId[result.storeId] = result;
        });
        self.travelTimes = byStoreId;
        self.writeCachedTravelTimes(origin, byStoreId);
        self.applyTravelTimeRanking();
      })
      .catch(function () {
        // Straight-line ranking is already on screen and is a perfectly good answer.
      });
  };

  /** Re-renders with drive times applied, keeping the highlighted store in step. */
  StoreFinder.prototype.applyTravelTimeRanking = function () {
    var previousTopId = this.nearestId;
    this.render();
    if (this.visibleStores.length === 0) return;

    var newTopId = this.nearestId;
    if (!newTopId || newTopId === previousTopId) return;
    if (!this.selectionIsAuto) return;

    this.fitToNearest();
    this.selectStore(newTopId, false, true);
  };

  StoreFinder.prototype.requestTravelTimes = function (endpoint, origin, entries, requestId) {
    var self = this;
    if (entries.length === 0) return Promise.resolve([]);

    var url = new URL(endpoint, window.location.href);
    url.searchParams.set(
      'origin',
      roundCoordinate(origin.latitude) + ',' + roundCoordinate(origin.longitude)
    );
    url.searchParams.set(
      'storeIds',
      entries
        .map(function (entry) {
          return entry.store.storeId;
        })
        .join(',')
    );

    return fetch(url.toString(), { method: 'GET' })
      .then(function (response) {
        if (!response.ok) throw new Error('Travel times failed (' + response.status + ')');
        return response.json();
      })
      .then(function (payload) {
        if (requestId !== self.travelTimeRequestId) return [];
        // A server with no routing token configured says so once; stop asking.
        if (payload && payload.unavailable) {
          self.travelTimesUnavailable = true;
          return [];
        }
        return payload && Array.isArray(payload.travelTimes) ? payload.travelTimes : [];
      });
  };

  StoreFinder.prototype.travelTimeCacheKey = function (origin) {
    return 'agape-store-finder-travel:' + originCacheKey(origin);
  };

  StoreFinder.prototype.readCachedTravelTimes = function (origin) {
    try {
      var raw = window.sessionStorage.getItem(this.travelTimeCacheKey(origin));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  StoreFinder.prototype.writeCachedTravelTimes = function (origin, byStoreId) {
    try {
      window.sessionStorage.setItem(this.travelTimeCacheKey(origin), JSON.stringify(byStoreId));
    } catch (error) {
      // Private browsing or a full quota — the finder just re-requests next time.
    }
  };

  /**
   * Search accepts a ZIP code (resolved to a ZIP3 centroid, which is close enough to
   * rank stores that are tens of miles apart) or free text matched against the store
   * name, city, state, and postal code.
   */
  StoreFinder.prototype.applySearch = function (rawValue) {
    var value = (rawValue || '').trim();
    var self = this;

    if (this.searchTerm === value) return;
    this.searchTerm = value;

    var zipMatch = value.match(/^(\d{5})(?:-\d{4})?$/);
    if (zipMatch) {
      this.lookupZip(zipMatch[1])
        .then(function (point) {
          if (self.searchTerm !== value) return;
          if (point) {
            self.setStatus('');
            self.setOrigin(point[0], point[1], false);
          } else {
            self.render();
            self.fitToVisible();
          }
        })
        .catch(function () {
          if (self.searchTerm !== value) return;
          self.render();
          self.fitToVisible();
        });
      return;
    }

    this.render();
    // Text search filters the list only — the map keeps every store marker in place.
    if (this.origin && !this.selectedId && this.visibleStores.length > 0) {
      this.selectStore(this.visibleStores[0].storeId, false, true);
    }
  };

  StoreFinder.prototype.lookupZip = function (zip) {
    var self = this;
    var prefix = zip.slice(0, 3);

    if (this.zip3) {
      return Promise.resolve(this.zip3[prefix] || null);
    }

    return fetch(this.config.zip3Url, { method: 'GET' })
      .then(function (response) {
        if (!response.ok) throw new Error('Failed to load ZIP centroids');
        return response.json();
      })
      .then(function (payload) {
        self.zip3 = (payload && payload.centroids) || {};
        return self.zip3[prefix] || null;
      });
  };

  StoreFinder.prototype.matchesSearch = function (store) {
    if (!this.searchTerm) return true;
    // A resolved ZIP re-ranks by distance rather than filtering the list down.
    if (/^\d{5}(?:-\d{4})?$/.test(this.searchTerm) && this.origin) return true;

    var needle = this.searchTerm.toLowerCase();
    var haystack = [store.name, store.city, store.region, store.postalCode, store.retailAccountName]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.indexOf(needle) !== -1;
  };

  StoreFinder.prototype.findStore = function (storeId) {
    for (var i = 0; i < this.stores.length; i++) {
      if (this.stores[i].storeId === storeId) return this.stores[i];
    }
    return null;
  };

  /**
   * Moves the selected store to the head of the list, adding it if the ranked slice or
   * the search filtered it out.
   *
   * Markers cover every store, so a marker click can land on one that isn't in the list
   * at all — and burying it further down would mean scrolling the map off screen to
   * reach it. At the top, the card is a short scroll away and the map stays put.
   * `nearestId` carries the ranking's own top store so the "nearest/quickest" flag
   * doesn't follow the selection up here.
   */
  StoreFinder.prototype.promoteSelected = function (list, decorate) {
    this.nearestId = list.length > 0 ? list[0].storeId : null;
    if (!this.selectedId) return list;

    var selectedId = this.selectedId;
    var selected = null;
    var rest = list.filter(function (store) {
      if (store.storeId !== selectedId) return true;
      selected = store;
      return false;
    });

    if (!selected) {
      var store = this.findStore(selectedId);
      if (!store) return list;
      selected = decorate ? decorate(store) : store;
    }

    return [selected].concat(rest);
  };

  StoreFinder.prototype.computeVisibleStores = function () {
    var self = this;
    var matched = this.stores.filter(function (store) {
      return self.matchesSearch(store);
    });

    if (!this.origin) {
      return this.promoteSelected(
        matched.slice().sort(function (left, right) {
          return left.name.localeCompare(right.name);
        })
      );
    }

    var withDistance = matched.map(function (store) {
      return Object.assign({}, store, {
        distanceMiles: distanceMiles(
          self.origin.latitude,
          self.origin.longitude,
          store.latitude,
          store.longitude
        )
      });
    });
    // Drive time wins where we have it: the closest store as the crow flies is often not
    // the quickest to reach. Stores without a drive time keep their straight-line order
    // and sort below every routed store, so a pending or failed lookup only ever costs
    // precision, never correctness.
    var durations = this.travelTimes;
    withDistance.forEach(function (store) {
      var travel = durations[store.storeId];
      if (travel) {
        store.durationSeconds = travel.durationSeconds;
      }
    });
    withDistance.sort(function (left, right) {
      var leftDuration = left.durationSeconds;
      var rightDuration = right.durationSeconds;
      if (leftDuration !== undefined && rightDuration !== undefined) {
        return leftDuration - rightDuration;
      }
      if (leftDuration !== undefined) return -1;
      if (rightDuration !== undefined) return 1;
      return left.distanceMiles - right.distanceMiles;
    });

    var limit = Number(this.config.nearbyCount);
    return this.promoteSelected(
      withDistance.slice(0, (isFinite(limit) ? limit : 10) + 1),
      function (store) {
        var travel = durations[store.storeId];
        return Object.assign({}, store, {
          distanceMiles: distanceMiles(
            self.origin.latitude,
            self.origin.longitude,
            store.latitude,
            store.longitude
          ),
          durationSeconds: travel ? travel.durationSeconds : undefined
        });
      }
    );
  };

  StoreFinder.prototype.render = function () {
    this.visibleStores = this.computeVisibleStores();
    this.renderList();
    this.renderMarkers();
  };

  StoreFinder.prototype.renderList = function () {
    if (!this.listEl) return;
    this.listEl.textContent = '';

    if (this.visibleStores.length === 0) {
      if (this.emptyEl) {
        // A failed load must not read as "no stores exist" — that is a different fact.
        this.emptyEl.textContent = this.loadFailed
          ? this.strings.loadError || ''
          : this.stores.length === 0
            ? this.strings.empty || ''
            : this.strings.searchNoMatch || '';
        this.emptyEl.hidden = false;
      }
      return;
    }

    if (this.emptyEl) this.emptyEl.hidden = true;

    var self = this;
    this.visibleStores.forEach(function (store) {
      self.listEl.appendChild(
        self.buildListItem(store, Boolean(self.origin) && store.storeId === self.nearestId)
      );
    });
  };

  StoreFinder.prototype.buildListItem = function (store, isNearest) {
    var self = this;
    var item = document.createElement('li');
    item.className = 'agape-store-finder__item';
    // Status drives the selected-card colour, which has to agree with the marker's.
    item.classList.add(
      'agape-store-finder__item--' + (store.status === 'live' ? 'live' : 'soon')
    );
    if (isNearest) item.classList.add('agape-store-finder__item--nearest');
    if (store.storeId === this.selectedId) item.classList.add('is-selected');
    item.setAttribute('data-store-id', store.storeId);

    var card = document.createElement('div');
    card.className = 'agape-store-finder__card';

    if (isNearest) {
      // Once drive times are in, the top card is the quickest to reach rather than the
      // closest, and the label has to say which claim it is actually making.
      var flagText =
        typeof store.durationSeconds === 'number'
          ? this.strings.quickest || this.strings.nearest
          : this.strings.nearest;
      if (flagText) {
        var flag = document.createElement('p');
        flag.className = 'agape-store-finder__flag';
        flag.textContent = flagText;
        card.appendChild(flag);
      }
    }

    var select = document.createElement('button');
    select.type = 'button';
    select.className = 'agape-store-finder__select';
    select.addEventListener('click', function () {
      self.selectStore(store.storeId, true);
    });

    var name = document.createElement('span');
    name.className = 'agape-store-finder__name';
    name.textContent = store.name;
    select.appendChild(name);

    var meta = document.createElement('span');
    meta.className = 'agape-store-finder__meta';

    var status = document.createElement('span');
    status.className =
      'agape-store-finder__status-pill agape-store-finder__status-pill--' +
      (store.status === 'live' ? 'live' : 'soon');
    status.textContent =
      store.status === 'live' ? this.strings.available || '' : this.strings.comingSoon || '';
    meta.appendChild(status);

    if (typeof store.durationSeconds === 'number') {
      var duration = document.createElement('span');
      duration.className = 'agape-store-finder__duration';
      duration.textContent = formatDuration(store.durationSeconds) + ' drive';
      meta.appendChild(duration);
    }

    if (typeof store.distanceMiles === 'number') {
      var distance = document.createElement('span');
      distance.className = 'agape-store-finder__distance';
      distance.textContent = formatDistance(store.distanceMiles);
      meta.appendChild(distance);
    }

    select.appendChild(meta);

    var address = document.createElement('span');
    address.className = 'agape-store-finder__address';
    address.textContent = formatAddress(store) || formatLocality(store);
    if (address.textContent) select.appendChild(address);

    card.appendChild(select);

    var directions = document.createElement('a');
    directions.className = 'agape-store-finder__directions';
    directions.href = directionsUrl(store);
    directions.target = '_blank';
    directions.rel = 'noopener noreferrer';
    directions.textContent = this.strings.directions || 'Directions';
    card.appendChild(directions);

    item.appendChild(card);
    return item;
  };

  StoreFinder.prototype.renderMarkers = function () {
    if (!this.map || !window.L) return;

    var self = this;
    var nextIds = {};

    // Map markers always reflect the full store set; search only filters the list.
    this.stores.forEach(function (store) {
      nextIds[store.storeId] = true;
      var isSelected = store.storeId === self.selectedId;
      if (self.markers[store.storeId]) {
        self.markers[store.storeId].setIcon(self.buildMarkerIcon(store, isSelected));
        return;
      }

      var marker = window.L.marker([store.latitude, store.longitude], {
        icon: self.buildMarkerIcon(store, isSelected),
        title: store.name,
        alt: store.name,
        riseOnHover: true
      });
      marker.on('click', function () {
        self.selectStore(store.storeId, false);
      });
      // Leaflet swallows dblclick on a marker, so the map's own zoom never fires there —
      // double-clicking a dot used to do nothing while dead space zoomed in.
      marker.on('dblclick', function (event) {
        if (window.L.DomEvent) window.L.DomEvent.stopPropagation(event);
        if (!self.map) return;
        self.map.setView(event.latlng || marker.getLatLng(), self.map.getZoom() + 1, {
          animate: true
        });
      });
      marker.bindPopup(self.buildPopup(store), { closeButton: true, offset: [0, -8] });
      marker.addTo(self.map);
      self.markers[store.storeId] = marker;
    });

    Object.keys(this.markers).forEach(function (storeId) {
      if (nextIds[storeId]) return;
      self.map.removeLayer(self.markers[storeId]);
      delete self.markers[storeId];
    });
  };

  StoreFinder.prototype.buildMarkerIcon = function (store, isSelected) {
    var classNames = ['agape-store-finder__marker'];
    classNames.push('agape-store-finder__marker--' + (store.status === 'live' ? 'live' : 'soon'));
    if (isSelected) classNames.push('is-selected');
    return window.L.divIcon({
      className: classNames.join(' '),
      html: '<span class="agape-store-finder__marker-dot"></span>',
      iconSize: [22, 22],
      iconAnchor: [11, 11],
      popupAnchor: [0, -6]
    });
  };

  StoreFinder.prototype.buildPopup = function (store) {
    var container = document.createElement('div');
    container.className = 'agape-store-finder__popup';

    var name = document.createElement('p');
    name.className = 'agape-store-finder__popup-name';
    name.textContent = store.name;
    container.appendChild(name);

    var address = formatAddress(store) || formatLocality(store);
    if (address) {
      var addressEl = document.createElement('p');
      addressEl.className = 'agape-store-finder__popup-address';
      addressEl.textContent = address;
      container.appendChild(addressEl);
    }

    if (store.status !== 'live' && this.strings.comingSoon) {
      var status = document.createElement('p');
      status.className = 'agape-store-finder__popup-status';
      status.textContent = this.strings.comingSoon;
      container.appendChild(status);
    }

    var directions = document.createElement('a');
    directions.className = 'agape-store-finder__popup-link';
    directions.href = directionsUrl(store);
    directions.target = '_blank';
    directions.rel = 'noopener noreferrer';
    directions.textContent = this.strings.directions || 'Directions';
    container.appendChild(directions);

    return container;
  };

  /**
   * `isAuto` marks a selection the finder made on the visitor's behalf (the top result)
   * rather than one they clicked. Only auto selections follow a re-rank — moving the map
   * out from under someone who just picked a store would be hostile.
   */
  StoreFinder.prototype.selectStore = function (storeId, panMap, isAuto) {
    var self = this;
    this.selectedId = storeId;
    this.selectionIsAuto = Boolean(isAuto);

    // Re-render so the pick moves to the head of the list (promoteSelected) — it may not
    // have been in the list at all, and even when it was, the row has to come to the top.
    this.render();

    if (this.listEl) {
      var selectedItem = null;
      Array.prototype.forEach.call(
        this.listEl.querySelectorAll('.agape-store-finder__item'),
        function (item) {
          var isSelected = item.getAttribute('data-store-id') === storeId;
          item.classList.toggle('is-selected', isSelected);
          if (isSelected) selectedItem = item;
        }
      );
      // A list click already has the row under the cursor; scroll only when the pick came
      // from the map, and keep it to the list panel rather than jumping the page.
      if (selectedItem && !panMap) {
        this.scrollListTo(selectedItem);
      }
    }

    this.stores.forEach(function (store) {
      var marker = self.markers[store.storeId];
      if (!marker || !window.L) return;
      marker.setIcon(self.buildMarkerIcon(store, store.storeId === storeId));
    });

    var marker = this.markers[storeId];
    if (!marker || !this.map) return;

    if (panMap) {
      var targetZoom = Math.max(this.map.getZoom(), 10);
      this.map.setView(marker.getLatLng(), targetZoom, { animate: true });
    }
    marker.openPopup();
  };

  /**
   * Brings the selected row into view.
   *
   * The row is always the first one by the time this runs, so this is a short move in
   * both layouts: on desktop only the list panel scrolls, and below 900px — where the
   * list sits under the map and the page is the scroller — `block: 'nearest'` scrolls
   * the page the minimum needed to clear the card's top edge, leaving the map where it
   * is instead of dragging the visitor down the list. The items' scroll-margin keeps the
   * card out from under the fixed header.
   */
  StoreFinder.prototype.scrollListTo = function (item) {
    var panel = this.listEl.closest
      ? this.listEl.closest('.agape-store-finder__list-panel')
      : null;

    if (!panel || panel.scrollHeight <= panel.clientHeight) {
      if (item.scrollIntoView) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      return;
    }

    // Measured off rects rather than offsetTop: the panel is statically positioned, so
    // it is not necessarily the item's offsetParent.
    var panelRect = panel.getBoundingClientRect();
    var itemRect = item.getBoundingClientRect();
    var delta = 0;
    if (itemRect.top < panelRect.top) {
      delta = itemRect.top - panelRect.top - 8;
    } else if (itemRect.bottom > panelRect.bottom) {
      delta = itemRect.bottom - panelRect.bottom + 8;
    }
    if (delta === 0) return;

    var top = panel.scrollTop + delta;
    if (panel.scrollTo) {
      panel.scrollTo({ top: top, behavior: 'smooth' });
    } else {
      panel.scrollTop = top;
    }
  };

  StoreFinder.prototype.fitTo = function (stores, includeOrigin) {
    if (!this.map || !window.L || stores.length === 0) return;

    var bounds = window.L.latLngBounds(
      stores.map(function (store) {
        return [store.latitude, store.longitude];
      })
    );
    if (includeOrigin && this.origin) bounds.extend([this.origin.latitude, this.origin.longitude]);
    this.map.fitBounds(bounds, { padding: [48, 48], maxZoom: 12 });
  };

  StoreFinder.prototype.fitToVisible = function () {
    this.fitTo(this.visibleStores, true);
  };

  /** Frames the closest handful so a far-flung tail store can't zoom the map out. */
  StoreFinder.prototype.fitToNearest = function () {
    this.fitTo(this.visibleStores.slice(0, 5), true);
  };

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-store-finder]'), function (root) {
      if (root.dataset.storeFinderReady === 'true') return;
      root.dataset.storeFinderReady = 'true';
      new StoreFinder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections in place; re-initialize the new markup.
  document.addEventListener('shopify:section:load', init);
})();
